import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useInitiateAuthorizationMutation } from '../__generated__/apollo-hooks';
import { ERROR_MESSAGES, type ErrorEntry } from '../constants/error-messages';
import { NotificationContext } from '../contexts/notification.context';
import { safeSessionStorage } from '../helpers/local-storage.helper';
import {
  DEFAULT_CALLBACK_PARAMETERS,
  SCM_AUTH_SESSION_KEY
} from './scm-authorization.constants';

type UseScmAuthorizationPopupOptions = {
  onSuccess?: () => void;
  onError?: (error: ErrorEntry) => void;
};

type UseScmAuthorizationPopupResult = {
  authorize: (origin: string) => Promise<void>;
  isAuthorizing: boolean;
  error: ErrorEntry | null;
};

type AuthMessage =
  | { type: 'scm-auth-success'; origin: string }
  | { type: 'scm-auth-error'; origin: string; error: string };

const isAuthMessage = (data: unknown): data is AuthMessage => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const message = data as Record<string, unknown>;
  return (
    (message.type === 'scm-auth-success' ||
      message.type === 'scm-auth-error') &&
    typeof message.origin === 'string'
  );
};

/**
 * Timeout duration for authorization flow in milliseconds.
 * If authorization isn't completed within this time (e.g., user closes popup),
 * the hook will reset to allow retry.
 */
const AUTHORIZATION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Interval used to poll whether the user closed the popup without completing
 * the OAuth flow. Without this, the hook would stay stuck in `isAuthorizing`
 * until `AUTHORIZATION_TIMEOUT_MS` elapses, hiding the Connect CTA the whole
 * time.
 */
const POPUP_CLOSED_POLL_MS = 500;

/**
 * Hook that manages popup-based OAuth flow for SCM authorization.
 *
 * Flow:
 * 1. User calls `authorize(origin)` - e.g., `authorize('github.com')`
 * 2. Hook immediately opens a blank popup (to avoid popup blockers)
 * 3. Hook calls `initiateAuthorization` mutation with origin and redirectUri (just the origin)
 * 4. Hook navigates popup to the authorizationUrl (authorizationId is in the state param)
 * 5. Hook listens for postMessage from popup callback page
 * 6. On success message: calls `onSuccess`, clears state
 * 7. On error message: sets error state, then calls `onError` if supplied —
 *    otherwise renders a default error toast itself
 * 8. If user closes the popup: detected within `POPUP_CLOSED_POLL_MS`, resets
 *    state with the `authCancelled` entry so the consumer can retry
 * 9. Fallback: after `AUTHORIZATION_TIMEOUT_MS` resets state to allow retry
 */
export const useScmAuthorizationPopup = (
  options: UseScmAuthorizationPopupOptions = {}
): UseScmAuthorizationPopupResult => {
  const { onSuccess, onError } = options;
  const { renderNotification } = useContext(NotificationContext);

  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [error, setError] = useState<ErrorEntry | null>(null);

  const popupRef = useRef<Window | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [initiateAuthorization] = useInitiateAuthorizationMutation();

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
    safeSessionStorage.removeItem(SCM_AUTH_SESSION_KEY);
  }, []);

  const handleAuthComplete = useCallback(
    (success: boolean, errorEntry?: ErrorEntry) => {
      if (!timeoutRef.current) {
        return;
      }
      cleanup();
      setIsAuthorizing(false);

      if (success) {
        setError(null);
        onSuccess?.();
        return;
      }

      const entry = errorEntry ?? ERROR_MESSAGES.scmAuth.authorizeFailed;
      setError(entry);
      if (onError) {
        onError(entry);
      } else {
        renderNotification('error', entry.description ?? '', {
          title: entry.title,
          placement: { vertical: 'top', horizontal: 'center' },
          duration: null
        });
      }
    },
    [cleanup, onSuccess, onError, renderNotification]
  );

  // Listen for postMessage from popup callback page
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate message origin matches our application origin
      if (event.origin !== window.location.origin) {
        return;
      }

      if (!isAuthMessage(event.data)) {
        return;
      }

      const message = event.data;

      if (message.type === 'scm-auth-success') {
        handleAuthComplete(true);
      } else if (message.type === 'scm-auth-error') {
        handleAuthComplete(false, {
          title: ERROR_MESSAGES.scmAuth.authorizeFailed.title,
          description: message.error
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleAuthComplete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const authorize = useCallback(
    async (origin: string): Promise<void> => {
      // Reset state for new authorization attempt
      setError(null);
      setIsAuthorizing(true);

      // Set timeout to reset authorization state if user doesn't complete flow
      // This handles cases where user closes popup without completing OAuth
      timeoutRef.current = setTimeout(() => {
        handleAuthComplete(false, ERROR_MESSAGES.scmAuth.authTimedOut);
      }, AUTHORIZATION_TIMEOUT_MS);

      // Open popup immediately to avoid popup blockers
      // Browsers allow popups in direct response to user action
      const popup = window.open(
        'about:blank',
        'scm-auth-popup',
        'width=600,height=700'
      );

      if (!popup) {
        handleAuthComplete(false, ERROR_MESSAGES.scmAuth.popupBlocked);
        return;
      }

      popupRef.current = popup;

      pollIntervalRef.current = setInterval(() => {
        if (popupRef.current?.closed) {
          handleAuthComplete(false, ERROR_MESSAGES.scmAuth.authCancelled);
        }
      }, POPUP_CLOSED_POLL_MS);

      try {
        // Use just the origin as redirectUri - callback handling is done by
        // ScmCallbackHandler component which is mounted at the app level
        const redirectUri = window.location.origin;

        const { data } = await initiateAuthorization({
          variables: {
            input: {
              origin,
              redirectUri
            }
          }
        });

        if (!data?.initiateAuthorization) {
          handleAuthComplete(false, ERROR_MESSAGES.scmAuth.authorizeFailed);
          return;
        }

        const { authorizationUrl, id, callbackParameters } =
          data.initiateAuthorization;

        // Store auth data in sessionStorage so the callback handler can
        // retrieve the authorizationId and know which params to extract.
        // This is required for OAuth 1.0a where the state param is not
        // a base64-encoded JSON object containing the authorizationId.
        const resolvedCallbackParameters =
          callbackParameters ?? DEFAULT_CALLBACK_PARAMETERS;

        const sessionStored = safeSessionStorage.setItem(
          SCM_AUTH_SESSION_KEY,
          JSON.stringify({
            authorizationId: id,
            callbackParameters: resolvedCallbackParameters
          })
        );

        // OAuth 2.0 encodes the authorizationId in `state` and the callback
        // reads it back from there, so a dropped write costs it nothing. OAuth
        // 1.0a has no `state` and can only fail, so bail before the popup
        // navigates rather than sending the user through consent that cannot
        // complete.
        if (!sessionStored && !resolvedCallbackParameters.includes('state')) {
          handleAuthComplete(false, ERROR_MESSAGES.scmAuth.storageUnavailable);
          return;
        }

        // Navigate popup to the OAuth authorization URL
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.location.href = authorizationUrl;
        } else {
          handleAuthComplete(false, ERROR_MESSAGES.scmAuth.authCancelled);
          return;
        }
      } catch (err) {
        // Known failure shapes above resolve through handleAuthComplete
        // directly with real copy — this only catches genuinely unexpected
        // errors (network/GraphQL failures), so surfacing the raw message is
        // the best available copy here.
        const message =
          err instanceof Error ? err.message : 'Failed to start authorization';
        handleAuthComplete(false, {
          title: ERROR_MESSAGES.scmAuth.authorizeFailed.title,
          description: message
        });
      }
    },
    [initiateAuthorization, handleAuthComplete]
  );

  return {
    authorize,
    isAuthorizing,
    error
  };
};
